import {
  types, applySnapshot, flow, cast,
} from 'mobx-state-tree';
import flatten from 'lodash/flatten';
import isUndefined from 'lodash/isUndefined';
import { notification } from '@routific/hive';
import { getRouteIdleItems, getRoutePauseItems } from '../../components/routes/RoutesGantt/RoutesGanttChart/helpers/routeGanttFormats';
import { EventType } from '../../components/routes/RoutesGantt/constants';
import getAppRoot from './utils/getAppRoot';
import { RouteDtoModel, RouteDtoModelType } from './models';
import { RouteSort, RouteSortDirection, RouteSortKey } from '../../types/TRouteSortKey';
import {
  getRouteStopItems, getRouteShiftItems, getRouteGanttDefaultStart, getRouteGanttDefaultEnd,
  TINY_STOP_SIZE_DEFAULT, CLUSTER_RANGE_DEFAULT, getRouteLineSegments,
} from '../../components/routes/RoutesGantt/RoutesGanttChart/helpers';
import { CustomRouteItem } from '../../components/routes/RoutesGantt/RoutesGanttChart';
import { rearrangeStopFailedNotification } from '../../notifications/scheduling/rearrangeStop';
import { assignStopsFailedNotification } from '../../notifications/scheduling/assignStops';
import cloneDeep from 'lodash/cloneDeep';
import { v4 as uuidv4 } from 'uuid';
import { logglyLogger } from '../../services/logglyLogger';
import { getTimelineStopNumberPreference, setTimelineStopNumberPreference } from './utils/timelineStopNumberPreference';
import { getTimelineRouteStatsPreference, setTimelineRouteStatsPreference } from './utils/timelineRouteStatsPreference';
import { getTimelineRouteSortPreference, setTimelineRouteSortPreference } from './utils/timelineRouteSortPreference';
import { getTimelineDragSelectionState } from '../../components/routes/RoutesGantt/RoutesGanttChart/dragSelection';
import compareRoutes from '../../helpers/sort/compareRoutes';

/**
 * Re-orders `routes` to match a previously settled id order. Routes that weren't in the
 * snapshot (created since) go to the bottom in their incoming (name) order; ids no longer
 * present are dropped.
 */
const applySettledOrder = (routes: RouteDtoModelType[], settledIds: string[]): RouteDtoModelType[] => {
  const byId = new Map(routes.map((r) => [r.id, r]));
  const settled = settledIds.map((id) => byId.get(id)).filter((r): r is RouteDtoModelType => !!r);
  const settledSet = new Set(settledIds);
  const unsettled = routes.filter((r) => !settledSet.has(r.id));
  return [...settled, ...unsettled];
};

const RouteSortKeyEnumType = types.enumeration('RouteSortKey', Object.values(RouteSortKey));
const RouteSortDirectionEnumType = types.enumeration('RouteSortDirection', Object.values(RouteSortDirection));

const Timeline = types.model('Timeline')
  .props({
    enableClustering: types.optional(types.boolean, true),
    tinyStopSize: types.optional(types.number, TINY_STOP_SIZE_DEFAULT),
    clusterRange: types.optional(types.number, CLUSTER_RANGE_DEFAULT),
    draggingVisit: types.maybe(types.frozen<CustomRouteItem>()),
    targetRoute: types.maybe(types.safeReference(types.late(() => RouteDtoModel))),
    draggableVisitIds: types.maybe(types.array(types.string)),
    hoveredRoute: types.maybe(types.string),
    newPositionTargetIndex: types.maybe(types.number),
    toggledPopup: types.optional(types.boolean, false),
    toggledStopNumbers: types.optional(types.boolean, () => getTimelineStopNumberPreference()),
    // Whether the route stats columns in the gantt sidebar are shown (vs collapsed away).
    routeStatsExpanded: types.optional(types.boolean, () => getTimelineRouteStatsPreference()),
    // How the timeline and routes list order routes (key + direction, chosen from the Routes page
    // header). A per-browser preference like the two above, persisted together.
    routeSortBy: types.optional(RouteSortKeyEnumType, () => getTimelineRouteSortPreference().key),
    routeSortDirection: types.optional(RouteSortDirectionEnumType, () => getTimelineRouteSortPreference().direction),
    loading: types.optional(types.boolean, false),
  })
  .volatile(() => ({
    // Route ids in the order last "settled" (day load, optimisation complete, sort re-selected).
    // Holding the order here, rather than sorting live, keeps rows still while the dispatcher
    // edits. Only meaningful in the start-time modes: a name doesn't change under an edit, a
    // departure does.
    settledRouteIds: undefined as string[] | undefined,
  }))
  .views((self) => ({
    get routeSort(): RouteSort {
      return { key: self.routeSortBy as RouteSortKey, direction: self.routeSortDirection as RouteSortDirection };
    },
    get isSortedByStartTime(): boolean {
      return self.routeSortBy === RouteSortKey.PLANNED_START_TIME;
    },
    get compareRoutes() {
      return compareRoutes(self.routeSortBy as RouteSortKey, self.routeSortDirection as RouteSortDirection);
    },
  }))
  .views((self) => ({
    /** What the start-time order would be right now, from live route data. */
    get liveStartTimeOrder(): RouteDtoModelType[] {
      return [...getAppRoot(self).sortedRoutes].sort(self.compareRoutes);
    },
  }))
  .views((self) => ({
    get routes(): RouteDtoModelType[] {
      const { sortedRoutes } = getAppRoot(self);
      if (!self.isSortedByStartTime) {
        // sortedRoutes is already name-ascending; descending is just its mirror.
        return self.routeSortDirection === RouteSortDirection.DESC ? [...sortedRoutes].reverse() : sortedRoutes;
      }
      if (!self.settledRouteIds) return self.liveStartTimeOrder;
      return applySettledOrder(sortedRoutes, self.settledRouteIds);
    },
  }))
  .views((self) => ({
    get routeLineSegments(): CustomRouteItem[] {
      return flatten(self.routes
        .filter((r) => (r.startEvent?.departureTime || r.firstStopEvent?.startTime || r.endEvent?.arrivalTime || r.lastStopEvent?.startTime))
        .map((r) => getRouteLineSegments(r)));
    },
    get shiftItems(): CustomRouteItem[] {
      return self.routes
        .filter((r) => !isUndefined(r.shiftStartTime) && !isUndefined(r.shiftEndTime))
        .map((r) => getRouteShiftItems(r));
    },
    get defaultStartTime(): number {
      return getRouteGanttDefaultStart([...this.shiftItems, ...this.routeLineSegments], getAppRoot(self).ui.selectedDate);
    },
    get defaultEndTime(): number {
      return getRouteGanttDefaultEnd([...this.shiftItems, ...this.routeLineSegments]);
    },
    get deliveryStopItems(): CustomRouteItem[] {
      return flatten(self.routes.map((r) => getRouteStopItems(r)));
    },
  }))
  .views((self) => ({
    get items(): any {
      const idleItems = flatten(self.routes.map((r) => getRouteIdleItems(r)));
      const pauseItems = flatten(self.routes.map((r) => getRoutePauseItems(r)));
      const stopItemsWithoutIdleEvents = self.deliveryStopItems
        .filter((stopItem) => stopItem.eventType !== EventType.IDLING && stopItem.eventType !== EventType.PAUSED);
      return [...self.shiftItems, ...self.routeLineSegments, ...stopItemsWithoutIdleEvents, ...idleItems, ...pauseItems];
    },
    get draggableTimelineItems(): CustomRouteItem[] {
      if (self.draggableVisitIds && self.draggableVisitIds.length > 0) {
        const draggableVisitId = self.draggableVisitIds[0];
        return [this.items.find((item: any) => item.id === draggableVisitId)];
      }
      return [];
    },
  }))
  .actions((self) => ({
    reset: () => {
      applySnapshot(self, {});
      // Volatile state isn't part of the snapshot, so clear it explicitly.
      self.settledRouteIds = undefined;
    },
    /** Freeze the current start-time order. Call on load/optimise/sort change, never on local edits. */
    settleRouteOrder: () => {
      if (!self.isSortedByStartTime) {
        self.settledRouteIds = undefined;
        return;
      }
      self.settledRouteIds = self.liveStartTimeOrder.map((r) => r.id);
    },
    clearSettledRouteOrder: () => {
      self.settledRouteIds = undefined;
    },
    /**
     * Slot routes that arrived since the last settle (e.g. later pages of a paginated load)
     * into their sorted position without moving rows that are already settled. On a clean load
     * this yields the fully sorted order; if the dispatcher edited between pages, their rows
     * stay where they are.
     */
    mergeNewRoutesIntoSettledOrder() {
      if (!self.isSortedByStartTime) return;
      if (!self.settledRouteIds) {
        this.settleRouteOrder();
        return;
      }
      const routes = getAppRoot(self).sortedRoutes;
      const byId = new Map(routes.map((r) => [r.id, r]));
      const settledSet = new Set(self.settledRouteIds);
      const merged = self.settledRouteIds.filter((id) => byId.has(id));
      const compare = self.compareRoutes;
      routes
        .filter((r) => !settledSet.has(r.id))
        .sort(compare)
        .forEach((route) => {
          const insertAt = merged.findIndex((id) => compare(route, byId.get(id)!) < 0);
          if (insertAt === -1) merged.push(route.id);
          else merged.splice(insertAt, 0, route.id);
        });
      self.settledRouteIds = merged;
    },
    setRouteSort(key: RouteSortKey, direction: RouteSortDirection) {
      self.routeSortBy = key;
      self.routeSortDirection = direction;
      setTimelineRouteSortPreference({ key, direction });
      // Changing the sort is a settle point: freeze the new order right away so rows don't keep
      // re-sorting live on edits until the next load. Re-selecting the current option is how a
      // dispatcher re-sorts on demand.
      this.settleRouteOrder();
    },
    setClustering: (value: boolean) => {
      self.enableClustering = value;
    },
    setToggledPopup: (value: boolean) => {
      self.toggledPopup = value;
    },
    toggleStopNumbers: () => {
      const numbersVisible = !self.toggledStopNumbers;
      self.toggledStopNumbers = numbersVisible;
      setTimelineStopNumberPreference(numbersVisible);
    },
    toggleRouteStats: () => {
      const statsExpanded = !self.routeStatsExpanded;
      self.routeStatsExpanded = statsExpanded;
      setTimelineRouteStatsPreference(statsExpanded);
    },
    setTinyStopSize: (value: number) => {
      self.tinyStopSize = value;
    },
    setLoading: (value: boolean) => {
      self.loading = value;
    },
    setClusterRange: (value: number) => {
      self.clusterRange = value;
    },
    canDragVisit(): boolean {
      return getAppRoot(self).account.canMoveStopsPermission;
    },
    setDraggableVisitIds(visitId: string | null | undefined) {
      let draggableVisitIds: string[] = [];
      if (visitId) {
        draggableVisitIds = [visitId];
      }
      self.draggableVisitIds = cast(draggableVisitIds);
    },
    dragVisit(draggingVisit: CustomRouteItem | undefined) {
      if (this.canDragVisit()) {
        self.draggingVisit = cloneDeep(draggingVisit);
      }
    },
    handleVisitDragEnd(draggedVisit: CustomRouteItem | undefined, targetRoute: RouteDtoModelType | undefined) {
      if ((!this.canDragVisit()) || (!(draggedVisit && targetRoute)) || (draggedVisit.group === targetRoute.id)) {
        return;
      }
      this.setDraggableVisitIds(undefined);
      this.dragVisitsToTargetRoute(draggedVisit, targetRoute);
    },
    dragVisitsToTargetRoute: flow(function* (draggedVisit: CustomRouteItem | undefined, targetRoute: RouteDtoModelType | undefined) {
      const root = getAppRoot(self);
      const {
        moveOrdersToRoutes,
      } = root.model;

      if (draggedVisit?.routeTimeline && targetRoute?.uuid) {
        // Drag state is a deep clone, so read MST views from the live stop.
        const draggedStop = root.model.routeTimelineDtos.get(draggedVisit.routeTimeline.id);
        if (!draggedStop) return;

        const { orderUuids: draggedStopOrders } = getTimelineDragSelectionState(
          { ...draggedVisit, routeTimeline: draggedStop },
          root.mapView?.selectedItems?.stops ?? [],
          root.mapView?.selectedItems?.unscheduledOrders ?? [],
          targetRoute,
        );

        if (draggedStopOrders && draggedStopOrders.length > 0) {
          try {
            yield moveOrdersToRoutes(draggedStopOrders, [targetRoute.uuid], false);
            root.mapView.setSelectedItems(undefined);
            root.mapView.clearSelectedStopAnchorsByRoute();
            root.mapView.setLassoMode(null);
            root.mapView.exitDrawMode();
            root.ui.selectRouteEvent(undefined);
          } catch (err) {
            notification(assignStopsFailedNotification(uuidv4(), draggedStopOrders.length));
            logglyLogger.log({ error: 'moveOrdersToRoutes Failed', info: err.message });
          }
        }
      }
    }),
    setTargetRoute(routeId: string | undefined | null) {
      if (routeId) {
        self.targetRoute = getAppRoot(self).model.routeDtos.get(routeId);
      } else {
        self.targetRoute = undefined;
      }
    },
    setHoveredRoute(routeId: string | undefined) {
      self.hoveredRoute = routeId;
    },
    setNewPositionTargetIndex(targetIndex: number | undefined) {
      self.newPositionTargetIndex = targetIndex;
    },
    handleRearrangeVisit: flow(function* (draggedVisit: CustomRouteItem | undefined, targetIndex: number) {
      if (draggedVisit) {
        const {
          rearrangeStopWithinRoute,
        } = getAppRoot(self).model;

        if (draggedVisit.route?.uuid && draggedVisit.eventUuid && targetIndex) {
          try {
            yield rearrangeStopWithinRoute(draggedVisit.route.uuid, draggedVisit.eventUuid, targetIndex);
            getAppRoot(self).timeline.setTargetRoute(undefined);
          } catch (err) {
            // The backend rejects a move once the driver has arrived, and its message says so.
            // The Gantt used to swallow it and show the generic retry copy, which reads as a
            // transient failure for something that will never succeed.
            notification(rearrangeStopFailedNotification(
              getAppRoot(self),
              draggedVisit.eventUuid,
              draggedVisit.routeTimeline?.id,
              draggedVisit.routeTimeline?.stopSequence,
              draggedVisit.route?.name,
              err?.response?.errors?.[0]?.message,
            ));
          }
        }
      }
    }),
  }));

export default Timeline;
